import type {
  GameOutcome,
  GamePhase,
  PlayerState,
  RoleId,
  VoteResult,
} from "@cloudnest/one-night-ultimate-werewolf-engine";
import { outcomeReasonLabels, roleLabels } from "./labels";

/**
 * Structural subset of a viewer projection — both the engine's
 * ProjectedViewerState and the server-sent OneNightViewerState satisfy it.
 */
export interface OneNightLogView {
  readonly phase: GamePhase;
  readonly players: readonly PlayerState[];
  readonly seatLocked: boolean;
  readonly selectedRoleIds: readonly RoleId[];
  readonly rolePeekConfirmedPlayerIds: readonly string[];
  readonly announcerTimeline: readonly {
    readonly roleId: RoleId;
    readonly status: "pending" | "complete";
  }[];
  readonly nightRoleQueue: readonly {
    readonly roleId: RoleId;
    readonly status: "pending" | "complete";
  }[];
  readonly voteLockedPlayerIds: readonly string[];
  readonly voteResult: VoteResult | null;
  readonly autoVotePlayerIds: readonly string[];
  readonly revealedCardLocations: readonly (
    | {
        readonly kind: "player";
        readonly playerId: string;
        readonly cardId: string;
      }
    | {
        readonly kind: "center";
        readonly slotIndex: 0 | 1 | 2;
        readonly cardId: string;
      }
  )[];
  readonly visibleCards: Readonly<
    Record<string, { readonly roleId: RoleId | null }>
  >;
  readonly gameOutcome: GameOutcome | null;
}

export type OneNightLogPhase = "준비" | "밤" | "낮" | "투표" | "결과";

export interface OneNightLogEntry {
  readonly phase: OneNightLogPhase;
  readonly text: string;
}

// Log entries derive from PUBLIC viewer-state transitions only. The engine's
// `nightLog` carries actor:role mappings and must never feed this log — see
// docs/wireframes/one-night-ultimate-werewolf/06-edge-cases.html §06.2.
function logPhaseOf(phase: GamePhase): OneNightLogPhase {
  switch (phase) {
    case "setup":
    case "role-peek":
      return "준비";
    case "night":
      return "밤";
    case "day":
      return "낮";
    case "vote":
      return "투표";
    case "reveal":
    case "complete":
      return "결과";
  }
}

function roleLabel(roleId: RoleId): string {
  return roleLabels.get(roleId) ?? roleId;
}

function phaseTransitionText(prev: GamePhase, next: GamePhase): string | null {
  if (prev === next) return null;
  switch (next) {
    case "role-peek":
      return "카드 배정 완료 — 각자 역할을 확인합니다";
    case "night":
      return "밤이 시작되었습니다 — 모두 눈을 감습니다";
    case "day":
      return "낮이 밝았습니다 — 토론을 시작합니다";
    case "vote":
      return "투표창이 열렸습니다";
    case "reveal":
      return "투표가 마감되어 결과를 공개합니다";
    case "complete":
      return "라운드가 완료되었습니다";
    case "setup":
      return "다음 라운드 준비로 돌아갑니다";
  }
}

export function deriveLogEntries(
  prev: OneNightLogView | null,
  next: OneNightLogView,
): readonly OneNightLogEntry[] {
  const entries: OneNightLogEntry[] = [];
  const phase = logPhaseOf(next.phase);
  const playerNameById = new Map(
    next.players.map((player) => [player.id, player.name]),
  );

  if (prev === null) {
    entries.push({
      phase: "준비",
      text: `게임 생성 · ${next.players.length}인`,
    });
    return entries;
  }

  if (!prev.seatLocked && next.seatLocked) {
    entries.push({ phase: "준비", text: "자리 순서가 확정되었습니다" });
  }

  if (
    prev.selectedRoleIds.join("|") !== next.selectedRoleIds.join("|") &&
    next.selectedRoleIds.length > 0
  ) {
    entries.push({
      phase: "준비",
      text: `역할 구성 저장 · ${next.selectedRoleIds.length}장`,
    });
  }

  const phaseText = phaseTransitionText(prev.phase, next.phase);
  if (phaseText) {
    entries.push({ phase, text: phaseText });
  }

  if (
    next.phase === "role-peek" &&
    next.rolePeekConfirmedPlayerIds.length >
      prev.rolePeekConfirmedPlayerIds.length
  ) {
    entries.push({
      phase: "준비",
      text: `역할 확인 ${next.rolePeekConfirmedPlayerIds.length}/${next.players.length}`,
    });
  }

  for (const [index, step] of next.announcerTimeline.entries()) {
    const prevStep = prev.announcerTimeline[index];
    if (prevStep?.status === "pending" && step.status === "complete") {
      entries.push({
        phase: "밤",
        text: `아나운서: ${roleLabel(step.roleId)} 선언 완료`,
      });
    }
  }

  for (const [index, step] of next.nightRoleQueue.entries()) {
    const prevStep = prev.nightRoleQueue[index];
    if (prevStep?.status === "pending" && step.status === "complete") {
      entries.push({
        phase: "밤",
        text: `${roleLabel(step.roleId)} 단계 완료`,
      });
    }
  }

  if (next.voteLockedPlayerIds.length > prev.voteLockedPlayerIds.length) {
    entries.push({
      phase: "투표",
      text: `투표 ${next.voteLockedPlayerIds.length}/${next.players.length}`,
    });
  }

  if (!prev.voteResult && next.voteResult) {
    for (const line of next.voteResult.voteLines) {
      const voter = playerNameById.get(line.voterPlayerId) ?? "플레이어";
      const target = playerNameById.get(line.targetPlayerId) ?? "플레이어";
      entries.push({ phase: "결과", text: `${voter} → ${target}` });
    }
    if (next.autoVotePlayerIds.length > 0) {
      const names = next.autoVotePlayerIds
        .map((playerId) => playerNameById.get(playerId) ?? "플레이어")
        .join(", ");
      entries.push({ phase: "결과", text: `자동 본인 투표: ${names}` });
    }
  }

  for (const location of next.revealedCardLocations) {
    const already = prev.revealedCardLocations.some(
      (candidate) => candidate.cardId === location.cardId,
    );
    if (already) continue;
    const projected = next.visibleCards[location.cardId];
    const role = projected?.roleId ? roleLabel(projected.roleId) : "카드";
    const where =
      location.kind === "player"
        ? (playerNameById.get(location.playerId) ?? "플레이어")
        : `중앙 ${location.slotIndex + 1}`;
    entries.push({ phase: "결과", text: `${where} 공개 — ${role}` });
  }

  if (!prev.gameOutcome && next.gameOutcome) {
    entries.push({
      phase: "결과",
      text:
        outcomeReasonLabels.get(next.gameOutcome.reason) ??
        next.gameOutcome.reason,
    });
  }

  return entries;
}

export function deriveCumulativeLog(
  views: readonly OneNightLogView[],
): readonly (readonly OneNightLogEntry[])[] {
  const perStep: (readonly OneNightLogEntry[])[] = [];
  let cumulative: readonly OneNightLogEntry[] = [];
  let prev: OneNightLogView | null = null;
  for (const view of views) {
    cumulative = [...cumulative, ...deriveLogEntries(prev, view)];
    perStep.push(cumulative);
    prev = view;
  }
  return perStep;
}
